import api from './api';

export interface Event {
  id: number;
  family_id: string;
  name: string;
  event_date: string;
  event_type: 'wedding' | 'housewarming' | 'birthday' | 'graduation' | 'other';
  description?: string;
  is_active: boolean;
  created_by: number;
  created_at: string;
  updated_at: string;
}

export interface GiftGiver {
  id: number;
  family_id: string;
  full_name: string;
  relationship: 'family_internal' | 'family_external' | 'friend_bride' | 'friend_groom' | 'colleague' | 'business_partner' | 'other';
  phone?: string;
  email?: string;
  address?: string;
  social_media?: string;
  notes?: string;
  is_active: boolean;
  created_by: number;
  created_at: string;
  updated_at: string;
}

export interface Gift {
  id: number;
  family_id: string;
  event_id: number;
  giver_id: number;
  amount: number;
  gift_type: 'cash' | 'gift' | 'other';
  gift_name?: string;
  gift_value?: number;
  notes?: string;
  is_returned: boolean;
  return_date?: string;
  return_event_id?: number;
  return_amount?: number;
  return_notes?: string;
  created_by: number;
  created_at: string;
  updated_at: string;
  event_name?: string;
  event_date?: string;
  event_type?: string;
  giver_name?: string;
  relationship?: string;
}

export interface CreateEventRequest {
  name: string;
  event_date: string;
  event_type: 'wedding' | 'housewarming' | 'birthday' | 'graduation' | 'other';
  description?: string;
}

export interface UpdateEventRequest {
  name?: string;
  event_date?: string;
  event_type?: 'wedding' | 'housewarming' | 'birthday' | 'graduation' | 'other';
  description?: string;
  is_active?: boolean;
}

export interface CreateGiftGiverRequest {
  full_name: string;
  relationship: 'family_internal' | 'family_external' | 'friend_bride' | 'friend_groom' | 'colleague' | 'business_partner' | 'other';
  phone?: string;
  email?: string;
  address?: string;
  social_media?: string;
  notes?: string;
}

export interface UpdateGiftGiverRequest {
  full_name?: string;
  relationship?: 'family_internal' | 'family_external' | 'friend_bride' | 'friend_groom' | 'colleague' | 'business_partner' | 'other';
  phone?: string;
  email?: string;
  address?: string;
  social_media?: string;
  notes?: string;
  is_active?: boolean;
}

export interface CreateGiftRequest {
  event_id: number;
  giver_id: number;
  amount: number;
  gift_type: 'cash' | 'gift' | 'other';
  gift_name?: string;
  gift_value?: number;
  notes?: string;
}

export interface UpdateGiftRequest {
  event_id?: number;
  giver_id?: number;
  amount?: number;
  gift_type?: 'cash' | 'gift' | 'other';
  gift_name?: string;
  gift_value?: number;
  notes?: string;
  is_returned?: boolean;
  return_date?: string;
  return_event_id?: number;
  return_amount?: number;
  return_notes?: string;
}

export interface GiftStats {
  total: {
    amount: number;
    count: number;
  };
  byEvent: Array<{
    event_name: string;
    event_date: string;
    event_type: string;
    total_amount: number;
    gift_count: number;
  }>;
  byRelationship: Array<{
    relationship: string;
    total_amount: number;
    gift_count: number;
  }>;
  notReturnedCount: number;
}

class GiftService {
  // ===== EVENTS =====
  
  async getEvents(): Promise<{ success: boolean; data: Event[] }> {
    const response = await api.get('/api/gifts/events');
    return response.data;
  }

  async createEvent(data: CreateEventRequest): Promise<{ success: boolean; data: Event }> {
    const response = await api.post('/api/gifts/events', data);
    return response.data;
  }

  async updateEvent(id: number, data: UpdateEventRequest): Promise<{ success: boolean; data: Event }> {
    const response = await api.put(`/api/gifts/events/${id}`, data);
    return response.data;
  }

  async deleteEvent(id: number): Promise<{ success: boolean; message: string }> {
    const response = await api.delete(`/api/gifts/events/${id}`);
    return response.data;
  }

  // ===== GIFT GIVERS =====

  async getGiftGivers(params?: {
    page?: number;
    limit?: number;
    search?: string;
    relationship?: string;
    is_active?: boolean;
  }): Promise<{ 
    success: boolean; 
    data: GiftGiver[];
    pagination?: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    }
  }> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.relationship) queryParams.append('relationship', params.relationship);
    if (params?.is_active !== undefined) queryParams.append('is_active', params.is_active.toString());

    const response = await api.get(`/api/gifts/givers?${queryParams.toString()}`);
    return response.data;
  }

  async createGiftGiver(data: CreateGiftGiverRequest): Promise<{ success: boolean; data: GiftGiver }> {
    const response = await api.post('/api/gifts/givers', data);
    return response.data;
  }

  async updateGiftGiver(id: number, data: UpdateGiftGiverRequest): Promise<{ success: boolean; data: GiftGiver }> {
    const response = await api.put(`/api/gifts/givers/${id}`, data);
    return response.data;
  }

  async deleteGiftGiver(id: number): Promise<{ success: boolean; message: string }> {
    const response = await api.delete(`/api/gifts/givers/${id}`);
    return response.data;
  }

  // ===== GIFTS =====

  async getGifts(params?: {
    event_id?: number;
    giver_id?: number;
    is_returned?: boolean;
    page?: number;
    limit?: number;
    gift_type?: string;
  }): Promise<{ 
    success: boolean; 
    data: Gift[];
    pagination?: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    }
  }> {
    const queryParams = new URLSearchParams();
    if (params?.event_id) queryParams.append('event_id', params.event_id.toString());
    if (params?.giver_id) queryParams.append('giver_id', params.giver_id.toString());
    if (params?.is_returned !== undefined) queryParams.append('is_returned', params.is_returned.toString());
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.gift_type) queryParams.append('gift_type', params.gift_type);

    const response = await api.get(`/api/gifts?${queryParams.toString()}`);
    return response.data;
  }

  async createGift(data: CreateGiftRequest): Promise<{ success: boolean; data: Gift }> {
    const response = await api.post('/api/gifts', data);
    return response.data;
  }

  async updateGift(id: number, data: UpdateGiftRequest): Promise<{ success: boolean; data: Gift }> {
    const response = await api.put(`/api/gifts/${id}`, data);
    return response.data;
  }

  async deleteGift(id: number): Promise<{ success: boolean; message: string }> {
    const response = await api.delete(`/api/gifts/${id}`);
    return response.data;
  }

  // ===== STATISTICS =====

  async getStats(): Promise<{ success: boolean; data: GiftStats }> {
    const response = await api.get('/api/gifts/stats');
    return response.data;
  }
}

export const giftService = new GiftService(); 
